// Pure game data — no three.js imports, so the node balance test can load it directly.

export type SpellKind =
  | 'missile'
  | 'bomb'
  | 'nova'
  | 'chain'
  | 'orbit'
  | 'zone'
  | 'beam'
  | 'boomerang'
  | 'strike'
  | 'volley'
  | 'vortex'
  | 'aura'
  | 'burst'
  | 'drain'
  | 'rune'
  | 'wave';

export interface SpellStats {
  damage: number;
  cooldown: number;
  count: number;
  area: number;
  speed: number;
  duration: number;
  pierce: number;
  chains: number;
  knockback: number;
  tick: number;
  slow: number;
  range: number;
}

export type StatDelta = Partial<SpellStats>;

export interface SpellDef {
  id: string;
  name: string;
  kind: SpellKind;
  icon: string;
  color: string;
  desc: string;
  base: StatDelta;
  upgrades: StatDelta[];
  evolution: { passive: string; name: string; desc: string; color: string; stats: StatDelta };
}

export interface PlayerMods {
  might: number;
  haste: number;
  area: number;
  amount: number;
  moveSpeed: number;
  maxHp: number;
  magnet: number;
  growth: number;
  armor: number;
  regen: number;
  duration: number;
  crit: number;
  greed: number;
  luck: number;
  revive: number;
  reroll: number;
}

export interface PassiveDef {
  id: string;
  name: string;
  icon: string;
  color: string;
  desc: string;
  maxLevel: number;
  perLevel: Partial<PlayerMods>;
}

export interface CharacterDef {
  id: string;
  name: string;
  title: string;
  spell: string;
  mods: Partial<PlayerMods>;
  perk: string;
  robe: string;
  trim: string;
  unlockStage: number;
}

export interface ShopDef {
  id: string;
  name: string;
  icon: string;
  desc: string;
  maxRank: number;
  baseCost: number;
  perRank: Partial<PlayerMods>;
}

export type Behavior =
  | 'chase'
  | 'weave'
  | 'ranged'
  | 'charger'
  | 'exploder'
  | 'summoner'
  | 'splitter'
  | 'static'
  | 'boss';

export type BossAttack = 'slam' | 'ring' | 'charge' | 'summon' | 'spiral' | 'rain';

export interface EnemyDef {
  id: string;
  name: string;
  model: string;
  tint: string;
  glow: string;
  scale: number;
  hp: number;
  speed: number;
  damage: number;
  xp: number;
  radius: number;
  mass: number;
  behavior: Behavior;
  child?: string;
  title?: string;
  attacks?: BossAttack[];
  enraged?: BossAttack[];
}

export type HazardKind = 'eruption' | 'blizzard' | 'curse' | 'lightning' | 'rift' | 'sandstorm' | 'rockfall';

export interface HazardDef {
  kind: HazardKind;
  interval: number;
  damage: number;
  color: string;
  start: number;
}

export interface StageVisuals {
  sky: string;
  fog: string;
  fogNear: number;
  fogFar: number;
  ground: [string, string, string];
  detail: 'grass' | 'ash' | 'snow' | 'stone' | 'rock' | 'sand';
  cracks: string | null;
  hemiSky: string;
  hemiGround: string;
  hemi: number;
  sun: string;
  sunIntensity: number;
  props: Array<{ kind: string; count: number; solid: boolean }>;
  ambient: 'none' | 'embers' | 'snow' | 'motes' | 'rain';
  accent: string;
}

export interface StageDef {
  id: string;
  name: string;
  subtitle: string;
  duration: number;
  hpMult: number;
  dmgMult: number;
  rateMult: number;
  xpMult: number;
  // [enemyId, fromSec, weight, toSec?]
  roster: Array<[string, number, number, number?]>;
  swarmer: string;
  flyer: string;
  elite: string;
  boss: string;
  hazard: HazardDef | null;
  visuals: StageVisuals;
  music: { root: number; mode: number[]; tempo: number };
}

const T = '︎'; // force text (not emoji) presentation for symbol glyphs

export const SPELLS: SpellDef[] = [
  {
    id: 'arcane_bolt',
    name: 'Arcane Bolt',
    kind: 'missile',
    icon: '✦',
    color: '#8fb8ff',
    desc: 'Homing bolts seek the nearest foes.',
    base: { damage: 11, cooldown: 1.0, count: 1, speed: 13, pierce: 1, area: 1 },
    upgrades: [{ count: 1 }, { damage: 6 }, { cooldown: -0.2 }, { count: 1, pierce: 1 }, { damage: 10 }],
    evolution: {
      passive: 'quickening',
      name: 'Astral Barrage',
      desc: 'A relentless stream of piercing starbolts.',
      color: '#b8d0ff',
      stats: { damage: 30, cooldown: 0.45, count: 5, pierce: 3, speed: 17, area: 1.3 },
    },
  },
  {
    id: 'fireball',
    name: 'Fireball',
    kind: 'bomb',
    icon: '☄' + T,
    color: '#ff7a2f',
    desc: 'Hurls fireballs that explode on impact.',
    base: { damage: 18, cooldown: 2.0, count: 1, speed: 10, area: 1.6, duration: 0, tick: 0.5 },
    upgrades: [{ damage: 8 }, { count: 1 }, { area: 0.4 }, { cooldown: -0.4 }, { damage: 12, count: 1 }],
    evolution: {
      passive: 'might',
      name: 'Inferno Comet',
      desc: 'Comets that scorch the ground they strike.',
      color: '#ffb347',
      stats: { damage: 48, area: 2.5, cooldown: 1.5, count: 3, duration: 2, tick: 0.5 },
    },
  },
  {
    id: 'frost_nova',
    name: 'Frost Nova',
    kind: 'nova',
    icon: '❄' + T,
    color: '#8ff3ff',
    desc: 'A freezing pulse that slows and repels.',
    base: { damage: 9, cooldown: 2.8, count: 1, area: 3.6, knockback: 5, slow: 0.45, duration: 1.6 },
    upgrades: [
      { damage: 5 },
      { area: 0.6 },
      { cooldown: -0.4 },
      { damage: 7, slow: 0.15 },
      { area: 0.8, duration: 0.6 },
    ],
    evolution: {
      passive: 'expansion',
      name: 'Absolute Zero',
      desc: 'Freezes everything in a vast radius solid.',
      color: '#d6fbff',
      stats: { damage: 50, area: 6.5, cooldown: 2.2, slow: 1, duration: 1.6, knockback: 3 },
    },
  },
  {
    id: 'chain_lightning',
    name: 'Chain Lightning',
    kind: 'chain',
    icon: 'ϟ',
    color: '#c9a2ff',
    desc: 'Lightning that leaps between enemies.',
    base: { damage: 13, cooldown: 1.5, count: 1, chains: 3, range: 4.5 },
    upgrades: [{ chains: 1 }, { damage: 6 }, { count: 1 }, { cooldown: -0.3 }, { chains: 2, damage: 8 }],
    evolution: {
      passive: 'fortune',
      name: 'Tempest Lord',
      desc: 'Storms of forking lightning with endless reach.',
      color: '#e3ccff',
      stats: { damage: 24, cooldown: 0.9, count: 3, chains: 10, range: 6 },
    },
  },
  {
    id: 'spirit_orbs',
    name: 'Spirit Orbs',
    kind: 'orbit',
    icon: '◎',
    color: '#ffe58f',
    desc: 'Orbs that circle you, striking all they touch.',
    base: { damage: 9, cooldown: 3.2, count: 2, area: 2.4, duration: 3.2, speed: 3.2, tick: 0.45 },
    upgrades: [{ count: 1 }, { damage: 5 }, { duration: 0.8, speed: 0.6 }, { count: 1 }, { damage: 8, area: 0.4 }],
    evolution: {
      passive: 'chrono',
      name: 'Celestial Halo',
      desc: 'A permanent ring of radiant spirits.',
      color: '#fff2b8',
      stats: { damage: 30, count: 6, duration: 9999, cooldown: 0.1, area: 3.2, speed: 4.2, tick: 0.3 },
    },
  },
  {
    id: 'venom_pool',
    name: 'Venom Flask',
    kind: 'zone',
    icon: '☠' + T,
    color: '#8dff6a',
    desc: 'Shatters flasks that leave toxic pools.',
    base: { damage: 4, cooldown: 3.0, count: 1, area: 1.6, duration: 3, tick: 0.5, slow: 0.15, range: 8 },
    upgrades: [{ damage: 2 }, { count: 1 }, { area: 0.4, duration: 1 }, { cooldown: -0.5 }, { damage: 4, count: 1 }],
    evolution: {
      passive: 'bloom',
      name: 'Blight Garden',
      desc: 'Vast, choking bogs that cripple the horde.',
      color: '#c4ff8a',
      stats: { damage: 10, area: 2.6, duration: 4.5, count: 4, slow: 0.4 },
    },
  },
  {
    id: 'radiant_lance',
    name: 'Radiant Lance',
    kind: 'beam',
    icon: '✧',
    color: '#fff4c2',
    desc: 'A piercing beam fired where you move.',
    base: { damage: 16, cooldown: 1.7, count: 1, area: 0.9, range: 12, knockback: 2 },
    upgrades: [{ damage: 7 }, { count: 1 }, { area: 0.4 }, { cooldown: -0.35 }, { damage: 10, range: 3 }],
    evolution: {
      passive: 'iron',
      name: 'Solar Judgement',
      desc: 'Four blinding pillars of sunlight.',
      color: '#ffffff',
      stats: { damage: 40, count: 4, area: 1.6, range: 18, cooldown: 1.1 },
    },
  },
  {
    id: 'wind_scythe',
    name: 'Wind Scythe',
    kind: 'boomerang',
    icon: '➶',
    color: '#a8ffd8',
    desc: 'A spinning blade that returns to you.',
    base: { damage: 10, cooldown: 1.8, count: 1, speed: 13, area: 1, tick: 0.4, knockback: 2 },
    upgrades: [{ damage: 5 }, { count: 1 }, { speed: 3, area: 0.3 }, { cooldown: -0.3 }, { damage: 7 }],
    evolution: {
      passive: 'boots',
      name: 'Tempest Crescent',
      desc: 'Huge cyclone blades that shred everything.',
      color: '#d4fff0',
      stats: { damage: 24, count: 3, area: 1.8, speed: 18, cooldown: 1.2 },
    },
  },
  {
    id: 'earth_spike',
    name: 'Earth Spike',
    kind: 'strike',
    icon: '▲',
    color: '#d8a46a',
    desc: 'Stone spikes erupt beneath enemies.',
    base: { damage: 20, cooldown: 2.2, count: 2, area: 1.3, range: 11, knockback: 4 },
    upgrades: [{ count: 1 }, { damage: 9 }, { area: 0.4 }, { cooldown: -0.4 }, { count: 2, damage: 10 }],
    evolution: {
      passive: 'heart',
      name: "Titan's Wrath",
      desc: 'Mountains tear up from the earth.',
      color: '#ffcf8f',
      stats: { damage: 44, count: 6, area: 2.3, cooldown: 1.5, knockback: 8 },
    },
  },
  {
    id: 'shadow_dagger',
    name: 'Shadow Dagger',
    kind: 'volley',
    icon: '†',
    color: '#b27bff',
    desc: 'Rapid daggers thrown where you face.',
    base: { damage: 7, cooldown: 0.6, count: 1, speed: 22, pierce: 1, area: 1 },
    upgrades: [{ count: 1 }, { pierce: 1 }, { damage: 4 }, { cooldown: -0.12 }, { count: 2, damage: 4 }],
    evolution: {
      passive: 'echo',
      name: 'Thousand Shadows',
      desc: 'Blades fan out ahead and behind you.',
      color: '#d7b8ff',
      stats: { damage: 18, count: 6, pierce: 4, cooldown: 0.3 },
    },
  },
  {
    id: 'gravity_well',
    name: 'Gravity Well',
    kind: 'vortex',
    icon: '✺',
    color: '#7d6bff',
    desc: 'A vortex that drags foes in and crushes them.',
    base: { damage: 3, cooldown: 5.5, count: 1, area: 2.6, duration: 2.8, tick: 0.4, knockback: 6, range: 9 },
    upgrades: [{ damage: 2 }, { area: 0.6 }, { duration: 1 }, { cooldown: -1 }, { count: 1, damage: 2 }],
    evolution: {
      passive: 'lode',
      name: 'Event Horizon',
      desc: 'Collapsing singularities devour the horde.',
      color: '#b3a8ff',
      stats: { damage: 8, area: 4.2, duration: 4, count: 2, cooldown: 4, knockback: 10 },
    },
  },
  {
    id: 'sunfire_aura',
    name: 'Sunfire Aura',
    kind: 'aura',
    icon: '☀' + T,
    color: '#ffb347',
    desc: 'A searing aura that burns nearby foes and pushes them back.',
    base: { damage: 4, cooldown: 0, count: 1, area: 2.6, tick: 0.6, knockback: 10 },
    upgrades: [{ area: 0.4 }, { damage: 3 }, { tick: -0.1 }, { area: 0.5, damage: 2 }, { damage: 4, tick: -0.1 }],
    evolution: {
      passive: 'tome',
      name: 'Solar Corona',
      desc: 'A miniature sun burns around you.',
      color: '#ffd27a',
      stats: { damage: 16, area: 4.5, tick: 0.3, knockback: 14 },
    },
  },
  {
    id: 'crystal_shards',
    name: 'Crystal Shards',
    kind: 'burst',
    icon: '◆',
    color: '#ff7ad9',
    desc: 'Bursts of crystal shards fly out in every direction.',
    base: { damage: 10, cooldown: 1.6, count: 8, speed: 16, pierce: 1, area: 1, range: 9 },
    upgrades: [{ count: 2 }, { pierce: 1 }, { damage: 5 }, { cooldown: -0.4 }, { pierce: 2, damage: 7 }],
    evolution: {
      passive: 'prism',
      name: 'Prism Storm',
      desc: 'Endless rings of piercing prismatic shards.',
      color: '#ffb8ee',
      stats: { damage: 34, cooldown: 0.7, count: 16, pierce: 6, speed: 20, area: 1.3, range: 11 },
    },
  },
  {
    id: 'blood_siphon',
    name: 'Blood Siphon',
    kind: 'drain',
    icon: '♦' + T,
    color: '#ff3b5c',
    desc: 'Drains life from the nearest foes to heal you.',
    base: { damage: 11, cooldown: 1.4, count: 3, range: 6 },
    upgrades: [{ damage: 5 }, { count: 1 }, { cooldown: -0.4 }, { range: 1.5, damage: 6 }, { count: 2, damage: 8 }],
    evolution: {
      passive: 'pact',
      name: 'Crimson Feast',
      desc: 'Blood tethers drink deep from the whole horde.',
      color: '#ff7088',
      stats: { damage: 48, cooldown: 0.8, count: 10, range: 8 },
    },
  },
  {
    id: 'rune_trap',
    name: 'Rune Trap',
    kind: 'rune',
    icon: 'ᛉ',
    color: '#36e8c4',
    desc: 'Lays runes that explode when foes step on them.',
    base: { damage: 14, cooldown: 1.8, count: 2, area: 1.6, duration: 6, knockback: 5 },
    upgrades: [{ damage: 6 }, { count: 1 }, { area: 0.4 }, { cooldown: -0.4 }, { count: 1, damage: 8 }],
    evolution: {
      passive: 'runestone',
      name: 'Glyph Field',
      desc: 'A minefield of runes that shatters the horde.',
      color: '#9dffe6',
      stats: { damage: 44, count: 5, area: 2.6, cooldown: 1.2, duration: 8, knockback: 9 },
    },
  },
  {
    id: 'tidal_wave',
    name: 'Tidal Wave',
    kind: 'wave',
    icon: '≈',
    color: '#3d9bff',
    desc: 'A rushing wave that sweeps foes away.',
    base: { damage: 12, cooldown: 2.4, count: 1, area: 1.8, speed: 9, range: 10, knockback: 9 },
    upgrades: [{ damage: 6 }, { count: 1 }, { area: 0.4 }, { cooldown: -0.5 }, { damage: 9, range: 2 }],
    evolution: {
      passive: 'pearl',
      name: 'Leviathan Tide',
      desc: 'Waves crash outward in every direction.',
      color: '#8ccaff',
      stats: { damage: 36, count: 4, area: 2.6, cooldown: 1.6, range: 13, knockback: 14 },
    },
  },
];

export const PASSIVES: PassiveDef[] = [
  { id: 'might', name: 'Arcane Might', icon: '✪', color: '#ff6b6b', desc: '+10% damage', maxLevel: 5, perLevel: { might: 0.1 } },
  { id: 'quickening', name: 'Quickening', icon: '⧗', color: '#6bd5ff', desc: '-7% cooldown', maxLevel: 5, perLevel: { haste: 0.07 } },
  { id: 'expansion', name: 'Expansion', icon: '◌', color: '#ffd86b', desc: '+10% area', maxLevel: 5, perLevel: { area: 0.1 } },
  { id: 'echo', name: 'Echo Sigil', icon: '❖', color: '#c78bff', desc: '+1 projectile', maxLevel: 2, perLevel: { amount: 1 } },
  { id: 'boots', name: 'Swift Boots', icon: '»', color: '#7dffb2', desc: '+8% move speed', maxLevel: 5, perLevel: { moveSpeed: 0.08 } },
  { id: 'heart', name: 'Vital Heart', icon: '♥' + T, color: '#ff5d8f', desc: '+20 max HP', maxLevel: 5, perLevel: { maxHp: 20 } },
  { id: 'lode', name: 'Lodestone', icon: '⊛', color: '#8fa8ff', desc: '+25% pickup radius', maxLevel: 5, perLevel: { magnet: 0.25 } },
  { id: 'tome', name: "Scholar's Tome", icon: '✎', color: '#ffe9a8', desc: '+8% experience', maxLevel: 5, perLevel: { growth: 0.08 } },
  { id: 'iron', name: 'Iron Skin', icon: '◈', color: '#b8c4d6', desc: '+1 armor', maxLevel: 5, perLevel: { armor: 1 } },
  { id: 'bloom', name: 'Lifebloom', icon: '✿', color: '#9dff8a', desc: '+0.3 HP/s regen', maxLevel: 5, perLevel: { regen: 0.3 } },
  { id: 'chrono', name: 'Chronoglass', icon: '⧖', color: '#a8f0ff', desc: '+10% duration', maxLevel: 5, perLevel: { duration: 0.1 } },
  { id: 'fortune', name: 'Fortune Coin', icon: '¤', color: '#ffd24d', desc: '+4% crit, +10% gold', maxLevel: 5, perLevel: { crit: 0.04, greed: 0.1 } },
  { id: 'prism', name: 'Prism Lens', icon: '◭', color: '#ffa8ec', desc: '+5% crit chance', maxLevel: 5, perLevel: { crit: 0.05 } },
  { id: 'pact', name: 'Blood Pact', icon: '♰', color: '#e8485f', desc: '+6% damage, +10 max HP', maxLevel: 5, perLevel: { might: 0.06, maxHp: 10 } },
  { id: 'runestone', name: 'Runestone', icon: 'ᚦ', color: '#7fe8d0', desc: '+6% area & duration', maxLevel: 5, perLevel: { area: 0.06, duration: 0.06 } },
  { id: 'pearl', name: 'Moon Pearl', icon: '☾' + T, color: '#dfe8ff', desc: '+8% chance of a 4th choice', maxLevel: 5, perLevel: { luck: 0.08 } },
];

export const CHARACTERS: CharacterDef[] = [
  { id: 'aria', name: 'Aria', title: 'Arcanist', spell: 'arcane_bolt', mods: { growth: 0.1 }, perk: '+10% experience', robe: '#3b4aa8', trim: '#8fb8ff', unlockStage: 0 },
  { id: 'kael', name: 'Kael', title: 'Pyromancer', spell: 'fireball', mods: { might: 0.1 }, perk: '+10% damage', robe: '#8a2a1c', trim: '#ffb347', unlockStage: 1 },
  { id: 'nyx', name: 'Nyx', title: 'Shadowblade', spell: 'shadow_dagger', mods: { moveSpeed: 0.12 }, perk: '+12% move speed', robe: '#2a1c3f', trim: '#b27bff', unlockStage: 2 },
  { id: 'thora', name: 'Thora', title: 'Stormcaller', spell: 'chain_lightning', mods: { haste: 0.08 }, perk: '-8% cooldown', robe: '#253a5e', trim: '#c9a2ff', unlockStage: 3 },
  { id: 'brann', name: 'Brann', title: 'Earthwarden', spell: 'earth_spike', mods: { maxHp: 30, armor: 1 }, perk: '+30 HP, +1 armor', robe: '#5a4128', trim: '#d8a46a', unlockStage: 4 },
  { id: 'lyra', name: 'Lyra', title: 'Frostweaver', spell: 'frost_nova', mods: { area: 0.1, duration: 0.1 }, perk: '+10% area & duration', robe: '#1f5566', trim: '#8ff3ff', unlockStage: 5 },
];

export const SHOP: ShopDef[] = [
  { id: 'might', name: 'Might', icon: '✪', desc: '+5% damage', maxRank: 5, baseCost: 120, perRank: { might: 0.05 } },
  { id: 'vitality', name: 'Vitality', icon: '♥' + T, desc: '+10 max HP', maxRank: 5, baseCost: 100, perRank: { maxHp: 10 } },
  { id: 'haste', name: 'Haste', icon: '⧗', desc: '-3% cooldown', maxRank: 5, baseCost: 150, perRank: { haste: 0.03 } },
  { id: 'armor', name: 'Armor', icon: '◈', desc: '+1 armor', maxRank: 3, baseCost: 200, perRank: { armor: 1 } },
  { id: 'regen', name: 'Recovery', icon: '✿', desc: '+0.1 HP/s', maxRank: 3, baseCost: 160, perRank: { regen: 0.1 } },
  { id: 'speed', name: 'Swiftness', icon: '»', desc: '+4% move speed', maxRank: 3, baseCost: 120, perRank: { moveSpeed: 0.04 } },
  { id: 'magnet', name: 'Magnetism', icon: '⊛', desc: '+10% pickup radius', maxRank: 3, baseCost: 90, perRank: { magnet: 0.1 } },
  { id: 'growth', name: 'Wisdom', icon: '✎', desc: '+5% experience', maxRank: 5, baseCost: 130, perRank: { growth: 0.05 } },
  { id: 'greed', name: 'Greed', icon: '¤', desc: '+10% gold', maxRank: 5, baseCost: 80, perRank: { greed: 0.1 } },
  { id: 'area', name: 'Reach', icon: '◌', desc: '+4% area', maxRank: 3, baseCost: 170, perRank: { area: 0.04 } },
  { id: 'reroll', name: 'Insight', icon: '↻', desc: '+1 reroll per run', maxRank: 3, baseCost: 140, perRank: { reroll: 1 } },
  { id: 'revive', name: 'Phoenix Ash', icon: '✚', desc: 'Revive once per run', maxRank: 1, baseCost: 900, perRank: { revive: 1 } },
];

const e = (d: EnemyDef): [string, EnemyDef] => [d.id, d];

export const ENEMIES: Record<string, EnemyDef> = Object.fromEntries([
  // Verdant Hollow
  e({ id: 'sproutling', name: 'Sproutling', model: 'blob', tint: '#5fbf4a', glow: '#ffe066', scale: 0.8, hp: 7, speed: 2.3, damage: 5, xp: 1, radius: 0.42, mass: 1, behavior: 'chase' }),
  e({ id: 'gloombat', name: 'Gloom Bat', model: 'bat', tint: '#5e4888', glow: '#ff4d6d', scale: 0.8, hp: 5, speed: 3.8, damage: 4, xp: 1, radius: 0.38, mass: 0.6, behavior: 'weave' }),
  e({ id: 'thornspitter', name: 'Thornspitter', model: 'caster', tint: '#a8486a', glow: '#c6ff4d', scale: 0.9, hp: 14, speed: 2.0, damage: 7, xp: 2, radius: 0.5, mass: 1.2, behavior: 'ranged' }),
  e({ id: 'mosshulk', name: 'Moss Hulk', model: 'brute', tint: '#9aaa70', glow: '#ffd24d', scale: 1.0, hp: 42, speed: 1.6, damage: 10, xp: 5, radius: 0.85, mass: 4, behavior: 'chase' }),
  // Ashen Wastes
  e({ id: 'ash_crawler', name: 'Ash Crawler', model: 'blob', tint: '#d9a03a', glow: '#ff9a3c', scale: 0.85, hp: 9, speed: 2.5, damage: 6, xp: 1, radius: 0.44, mass: 1, behavior: 'chase' }),
  e({ id: 'cinder_imp', name: 'Cinder Imp', model: 'caster', tint: '#b8321f', glow: '#ffb02e', scale: 0.85, hp: 16, speed: 2.3, damage: 8, xp: 2, radius: 0.48, mass: 1, behavior: 'ranged' }),
  e({ id: 'ember_wisp', name: 'Ember Wisp', model: 'bomber', tint: '#ff6a00', glow: '#fff066', scale: 0.8, hp: 10, speed: 3.4, damage: 18, xp: 2, radius: 0.45, mass: 0.8, behavior: 'exploder' }),
  e({ id: 'ash_bat', name: 'Ash Bat', model: 'bat', tint: '#8e7fa6', glow: '#ff7a2f', scale: 0.85, hp: 8, speed: 4.0, damage: 6, xp: 1, radius: 0.4, mass: 0.6, behavior: 'weave' }),
  e({ id: 'magma_golem', name: 'Magma Golem', model: 'golem', tint: '#84503e', glow: '#ff5a1f', scale: 1.1, hp: 60, speed: 1.4, damage: 13, xp: 6, radius: 1.0, mass: 5, behavior: 'chase' }),
  // Frostfang Reach
  e({ id: 'frost_shard', name: 'Frost Shard', model: 'bomber', tint: '#3d8bcc', glow: '#bff6ff', scale: 0.7, hp: 10, speed: 2.6, damage: 6, xp: 1, radius: 0.42, mass: 1, behavior: 'chase' }),
  e({ id: 'frost_wolf', name: 'Frost Wolf', model: 'beast', tint: '#5b7d9c', glow: '#6ff0ff', scale: 0.95, hp: 22, speed: 3.0, damage: 9, xp: 3, radius: 0.6, mass: 1.6, behavior: 'charger' }),
  e({ id: 'snow_owl', name: 'Snow Owl', model: 'bat', tint: '#6a80a4', glow: '#6ff0ff', scale: 0.85, hp: 9, speed: 4.0, damage: 6, xp: 1, radius: 0.4, mass: 0.6, behavior: 'weave' }),
  e({ id: 'frost_wraith', name: 'Frost Wraith', model: 'wraith', tint: '#3486c8', glow: '#6ff0ff', scale: 0.95, hp: 20, speed: 2.4, damage: 9, xp: 2, radius: 0.5, mass: 1, behavior: 'ranged' }),
  e({ id: 'yeti', name: 'Yeti', model: 'brute', tint: '#6a85a4', glow: '#4fd8ff', scale: 1.15, hp: 80, speed: 1.7, damage: 14, xp: 6, radius: 0.95, mass: 5, behavior: 'chase' }),
  // Sunken Crypt
  e({ id: 'skeleton', name: 'Skeleton', model: 'skeleton', tint: '#d9d2bf', glow: '#7dffb0', scale: 0.95, hp: 12, speed: 2.5, damage: 7, xp: 1, radius: 0.45, mass: 1, behavior: 'chase' }),
  e({ id: 'wailing_ghost', name: 'Wailing Ghost', model: 'wraith', tint: '#9a8cff', glow: '#e0b3ff', scale: 0.85, hp: 10, speed: 3.6, damage: 7, xp: 1, radius: 0.42, mass: 0.6, behavior: 'weave' }),
  e({ id: 'necro_acolyte', name: 'Necro Acolyte', model: 'caster', tint: '#6c4f94', glow: '#9dff6a', scale: 1.0, hp: 28, speed: 1.9, damage: 8, xp: 4, radius: 0.5, mass: 1.2, behavior: 'summoner', child: 'skeleton' }),
  e({ id: 'bone_golem', name: 'Bone Golem', model: 'golem', tint: '#cfc6ad', glow: '#7dffb0', scale: 1.15, hp: 110, speed: 1.5, damage: 16, xp: 7, radius: 1.0, mass: 6, behavior: 'chase' }),
  // Storm Peaks
  e({ id: 'storm_sprite', name: 'Storm Sprite', model: 'bomber', tint: '#4fa3ff', glow: '#e8f6ff', scale: 0.8, hp: 14, speed: 3.6, damage: 20, xp: 2, radius: 0.45, mass: 0.8, behavior: 'exploder' }),
  e({ id: 'harpy', name: 'Harpy', model: 'bat', tint: '#8b7d6b', glow: '#ffe14d', scale: 1.0, hp: 14, speed: 4.0, damage: 8, xp: 1, radius: 0.45, mass: 0.7, behavior: 'weave' }),
  e({ id: 'crag_hound', name: 'Crag Hound', model: 'beast', tint: '#9c7d5e', glow: '#ffe14d', scale: 1.0, hp: 30, speed: 3.1, damage: 11, xp: 3, radius: 0.62, mass: 1.8, behavior: 'charger' }),
  e({ id: 'thunder_shaman', name: 'Thunder Shaman', model: 'caster', tint: '#5878b8', glow: '#7fd4ff', scale: 1.0, hp: 26, speed: 2.2, damage: 10, xp: 3, radius: 0.5, mass: 1.2, behavior: 'ranged' }),
  e({ id: 'stone_giant', name: 'Stone Giant', model: 'brute', tint: '#8b9099', glow: '#7fd4ff', scale: 1.3, hp: 150, speed: 1.6, damage: 18, xp: 8, radius: 1.1, mass: 7, behavior: 'chase' }),
  // Void Rift
  e({ id: 'void_mite', name: 'Void Mite', model: 'blob', tint: '#6c36a6', glow: '#ff3df2', scale: 0.7, hp: 14, speed: 2.8, damage: 8, xp: 1, radius: 0.4, mass: 0.8, behavior: 'chase' }),
  e({ id: 'void_splitter', name: 'Void Splitter', model: 'blob', tint: '#7a2eb0', glow: '#ff3df2', scale: 1.15, hp: 30, speed: 2.2, damage: 10, xp: 2, radius: 0.6, mass: 1.6, behavior: 'splitter', child: 'void_mite' }),
  e({ id: 'rift_stalker', name: 'Rift Stalker', model: 'beast', tint: '#5c3c94', glow: '#b84dff', scale: 1.0, hp: 34, speed: 3.2, damage: 12, xp: 3, radius: 0.62, mass: 1.8, behavior: 'charger' }),
  e({ id: 'abyss_eye', name: 'Abyss Eye', model: 'eye', tint: '#64287e', glow: '#ff3df2', scale: 1.0, hp: 30, speed: 2.0, damage: 12, xp: 3, radius: 0.55, mass: 1.2, behavior: 'ranged' }),
  e({ id: 'void_behemoth', name: 'Void Behemoth', model: 'golem', tint: '#583a8e', glow: '#b84dff', scale: 1.35, hp: 200, speed: 1.5, damage: 22, xp: 10, radius: 1.2, mass: 8, behavior: 'chase' }),
  // Dasht-e Lut
  e({ id: 'lut_scarab', name: 'Sand Scarab', model: 'blob', tint: '#3e2f5a', glow: '#ffd36b', scale: 0.75, hp: 15, speed: 2.9, damage: 8, xp: 1, radius: 0.4, mass: 0.8, behavior: 'chase' }),
  e({ id: 'lut_vulture', name: 'Desert Vulture', model: 'bat', tint: '#4a3326', glow: '#ffb347', scale: 0.95, hp: 15, speed: 4.1, damage: 8, xp: 1, radius: 0.45, mass: 0.7, behavior: 'weave' }),
  e({ id: 'lut_scorpion', name: 'Lut Scorpion', model: 'beast', tint: '#7a2e1c', glow: '#ff6a1f', scale: 0.95, hp: 36, speed: 3.3, damage: 12, xp: 3, radius: 0.62, mass: 1.8, behavior: 'charger' }),
  e({ id: 'desert_ghoul', name: 'Desert Ghoul', model: 'caster', tint: '#33402a', glow: '#c6ff4d', scale: 1.0, hp: 32, speed: 2.2, damage: 12, xp: 3, radius: 0.5, mass: 1.2, behavior: 'ranged' }),
  e({ id: 'kalut_golem', name: 'Kalut Golem', model: 'golem', tint: '#4a2818', glow: '#ffb347', scale: 1.35, hp: 230, speed: 1.5, damage: 23, xp: 10, radius: 1.2, mass: 8, behavior: 'chase' }),
  // Mazandaran Forest
  e({ id: 'div_spawn', name: 'Div Spawn', model: 'blob', tint: '#8a5a3a', glow: '#ff4d4d', scale: 0.8, hp: 16, speed: 2.9, damage: 9, xp: 1, radius: 0.42, mass: 0.9, behavior: 'chase' }),
  e({ id: 'forest_raven', name: 'Forest Raven', model: 'bat', tint: '#a49cc4', glow: '#ff4d6d', scale: 0.9, hp: 15, speed: 4.2, damage: 9, xp: 1, radius: 0.42, mass: 0.6, behavior: 'weave' }),
  e({ id: 'div_shaman', name: 'Div Shaman', model: 'caster', tint: '#7a4a6e', glow: '#ff6a1f', scale: 1.0, hp: 32, speed: 2.2, damage: 13, xp: 3, radius: 0.5, mass: 1.2, behavior: 'ranged' }),
  e({ id: 'div_witch', name: 'Sorceress', model: 'caster', tint: '#6e7a4a', glow: '#9dff6a', scale: 1.0, hp: 34, speed: 1.9, damage: 10, xp: 4, radius: 0.5, mass: 1.2, behavior: 'summoner', child: 'div_spawn' }),
  e({ id: 'horned_div', name: 'Horned Div', model: 'brute', tint: '#9a9080', glow: '#ff4d4d', scale: 1.35, hp: 260, speed: 1.6, damage: 25, xp: 10, radius: 1.15, mass: 8, behavior: 'chase' }),
  // Damavand
  e({ id: 'zahhak_serpent', name: "Zahhak's Serpent", model: 'blob', tint: '#3d6b3a', glow: '#c6ff4d', scale: 0.75, hp: 17, speed: 3.0, damage: 10, xp: 1, radius: 0.4, mass: 0.8, behavior: 'chase' }),
  e({ id: 'ash_harpy', name: 'Ash Harpy', model: 'bat', tint: '#5a4a4a', glow: '#ff7a2f', scale: 1.0, hp: 16, speed: 4.2, damage: 10, xp: 1, radius: 0.45, mass: 0.7, behavior: 'weave' }),
  e({ id: 'fire_div', name: 'Fire Div', model: 'bomber', tint: '#b8321f', glow: '#ffd24d', scale: 0.85, hp: 18, speed: 3.6, damage: 24, xp: 2, radius: 0.45, mass: 0.8, behavior: 'exploder' }),
  e({ id: 'serpent_charmer', name: 'Serpent Charmer', model: 'caster', tint: '#4a3a5e', glow: '#9dff6a', scale: 1.0, hp: 36, speed: 1.9, damage: 11, xp: 4, radius: 0.5, mass: 1.2, behavior: 'summoner', child: 'zahhak_serpent' }),
  e({ id: 'magma_div', name: 'Magma Div', model: 'golem', tint: '#6e3a2a', glow: '#ff5a1f', scale: 1.4, hp: 290, speed: 1.5, damage: 27, xp: 11, radius: 1.25, mass: 9, behavior: 'chase' }),
  // Bosses
  e({ id: 'elder_treant', name: 'Elder Treant', title: 'Warden of the Hollow', model: 'treant', tint: '#c49a6a', glow: '#c6ff4d', scale: 1, hp: 9000, speed: 1.9, damage: 22, xp: 0, radius: 2.0, mass: 999, behavior: 'boss', attacks: ['slam', 'summon', 'ring'], enraged: ['slam', 'ring', 'rain', 'summon'] }),
  e({ id: 'molten_colossus', name: 'Molten Colossus', title: 'Heart of the Ash', model: 'colossus', tint: '#82503a', glow: '#ff6a1f', scale: 1, hp: 17000, speed: 2.0, damage: 26, xp: 0, radius: 2.2, mass: 999, behavior: 'boss', attacks: ['charge', 'ring', 'slam'], enraged: ['charge', 'spiral', 'rain', 'slam'] }),
  e({ id: 'frost_matriarch', name: 'Frost Matriarch', title: 'Queen of the Long Night', model: 'matriarch', tint: '#5a8cbf', glow: '#6ff0ff', scale: 1, hp: 16000, speed: 2.4, damage: 28, xp: 0, radius: 2.0, mass: 999, behavior: 'boss', attacks: ['charge', 'rain', 'ring'], enraged: ['charge', 'spiral', 'rain', 'summon'] }),
  e({ id: 'lich_king', name: 'The Lich King', title: 'Lord of the Drowned Dead', model: 'lich', tint: '#665490', glow: '#9dff6a', scale: 1, hp: 21000, speed: 2.0, damage: 30, xp: 0, radius: 1.8, mass: 999, behavior: 'boss', attacks: ['summon', 'spiral', 'rain'], enraged: ['spiral', 'ring', 'summon', 'rain'] }),
  e({ id: 'thunder_titan', name: 'Thunder Titan', title: 'Voice of the Storm', model: 'titan', tint: '#808ba6', glow: '#7fd4ff', scale: 1, hp: 58000, speed: 2.1, damage: 32, xp: 0, radius: 2.3, mass: 999, behavior: 'boss', attacks: ['slam', 'rain', 'charge'], enraged: ['rain', 'spiral', 'charge', 'ring'] }),
  e({ id: 'hollow_star', name: 'The Hollow Star', title: 'Hunger Between Worlds', model: 'star', tint: '#4e2c80', glow: '#ff3df2', scale: 1, hp: 70000, speed: 2.2, damage: 34, xp: 0, radius: 2.2, mass: 999, behavior: 'boss', attacks: ['spiral', 'ring', 'summon', 'rain'], enraged: ['spiral', 'ring', 'charge', 'rain', 'summon'] }),
  e({ id: 'azhdaha', name: 'Azhdaha', title: 'Serpent of the Burning Sands', model: 'azhdaha', tint: '#5a1e2e', glow: '#ff9a3c', scale: 1, hp: 70000, speed: 2.3, damage: 36, xp: 0, radius: 2.2, mass: 999, behavior: 'boss', attacks: ['charge', 'spiral', 'rain'], enraged: ['charge', 'spiral', 'rain', 'summon'] }),
  e({ id: 'white_div', name: 'Div-e Sepid', title: 'Lord of Mazandaran', model: 'white_div', tint: '#e8e4da', glow: '#ff4d4d', scale: 1, hp: 80000, speed: 2.2, damage: 38, xp: 0, radius: 2.3, mass: 999, behavior: 'boss', attacks: ['slam', 'charge', 'summon'], enraged: ['slam', 'charge', 'ring', 'rain', 'summon'] }),
  e({ id: 'zahhak', name: 'Zahhak', title: 'The Serpent King', model: 'zahhak', tint: '#7a2e3e', glow: '#9dff6a', scale: 1, hp: 92000, speed: 2.2, damage: 40, xp: 0, radius: 2.0, mass: 999, behavior: 'boss', attacks: ['spiral', 'ring', 'summon', 'rain'], enraged: ['spiral', 'ring', 'charge', 'rain', 'summon'] }),
  // Breakable light source (drops pickups)
  e({ id: 'brazier', name: 'Brazier', model: 'brazier', tint: '#3b3530', glow: '#ffb347', scale: 1, hp: 1, speed: 0, damage: 0, xp: 0, radius: 0.5, mass: 999, behavior: 'static' }),
]);

export const STAGES: StageDef[] = [
  {
    id: 'verdant',
    name: 'Verdant Hollow',
    subtitle: 'A sunlit glade where the trees whisper',
    duration: 480,
    hpMult: 1,
    dmgMult: 1,
    rateMult: 1,
    xpMult: 1,
    roster: [
      ['sproutling', 0, 10],
      ['gloombat', 40, 6],
      ['thornspitter', 100, 3],
      ['mosshulk', 150, 2.5],
      ['sproutling', 300, 6],
    ],
    swarmer: 'sproutling',
    flyer: 'gloombat',
    elite: 'mosshulk',
    boss: 'elder_treant',
    hazard: null,
    visuals: {
      // Daytime: warm sun, sky-blue fill and a light haze instead of night fog.
      // Deep grass with straw patches: the pale, sunlit forest creatures must still stand out against it.
      sky: '#8fc4e6', fog: '#aac8c4', fogNear: 26, fogFar: 64,
      ground: ['#2c5622', '#5f7a2c', '#20421b'], detail: 'grass', cracks: null,
      hemiSky: '#cfe6ff', hemiGround: '#4d5e2c', hemi: 1.7, sun: '#fff0d4', sunIntensity: 3.2,
      props: [{ kind: 'tree', count: 34, solid: true }, { kind: 'rock', count: 12, solid: true }, { kind: 'grass', count: 90, solid: false }],
      ambient: 'none', accent: '#9dff8a',
    },
    music: { root: 220, mode: [0, 2, 3, 5, 7, 8, 10], tempo: 92 },
  },
  {
    id: 'ashen',
    name: 'Ashen Wastes',
    subtitle: 'The ground itself remembers fire',
    duration: 480,
    hpMult: 1.5,
    dmgMult: 1.2,
    rateMult: 1.08,
    xpMult: 1.05,
    roster: [
      ['ash_crawler', 0, 10],
      ['ash_bat', 35, 5],
      ['cinder_imp', 80, 3],
      ['ember_wisp', 120, 3],
      ['magma_golem', 170, 2.5],
    ],
    swarmer: 'ash_crawler',
    flyer: 'ash_bat',
    elite: 'magma_golem',
    boss: 'molten_colossus',
    hazard: { kind: 'eruption', interval: 6, damage: 16, color: '#ff4a1a', start: 45 },
    visuals: {
      sky: '#140806', fog: '#1d0b07', fogNear: 18, fogFar: 48,
      ground: ['#2a1b17', '#3b2620', '#170e0c'], detail: 'ash', cracks: '#ff4a12',
      hemiSky: '#ffb08a', hemiGround: '#2a0f08', hemi: 0.9, sun: '#ff9a6a', sunIntensity: 1.5,
      props: [{ kind: 'spire', count: 22, solid: true }, { kind: 'deadtree', count: 18, solid: true }, { kind: 'rock', count: 20, solid: true }, { kind: 'ember', count: 40, solid: false }],
      ambient: 'embers', accent: '#ff9a3c',
    },
    music: { root: 196, mode: [0, 1, 4, 5, 7, 8, 10], tempo: 104 },
  },
  {
    id: 'frost',
    name: 'Frostfang Reach',
    subtitle: 'Where the wind has teeth',
    duration: 480,
    hpMult: 2.1,
    dmgMult: 1.4,
    rateMult: 1.15,
    xpMult: 1.1,
    roster: [
      ['frost_shard', 0, 10],
      ['snow_owl', 30, 5],
      ['frost_wolf', 70, 4],
      ['frost_wraith', 110, 3],
      ['yeti', 160, 2.5],
    ],
    swarmer: 'frost_shard',
    flyer: 'snow_owl',
    elite: 'yeti',
    boss: 'frost_matriarch',
    hazard: { kind: 'blizzard', interval: 26, damage: 0, color: '#dff6ff', start: 60 },
    visuals: {
      sky: '#0b1622', fog: '#1a2a3a', fogNear: 16, fogFar: 44,
      ground: ['#cfdde8', '#e8f1f8', '#a9bccb'], detail: 'snow', cracks: null,
      hemiSky: '#d6ecff', hemiGround: '#5c7187', hemi: 1.0, sun: '#e6f2ff', sunIntensity: 1.5,
      props: [{ kind: 'pine', count: 34, solid: true }, { kind: 'icespike', count: 20, solid: true }, { kind: 'rock', count: 12, solid: true }],
      ambient: 'snow', accent: '#8ff3ff',
    },
    music: { root: 207.65, mode: [0, 2, 3, 5, 7, 9, 10], tempo: 96 },
  },
  {
    id: 'crypt',
    name: 'Sunken Crypt',
    subtitle: 'The drowned dead do not rest',
    duration: 480,
    hpMult: 2.8,
    dmgMult: 1.6,
    rateMult: 1.22,
    xpMult: 1.15,
    roster: [
      ['skeleton', 0, 10],
      ['wailing_ghost', 30, 5],
      ['necro_acolyte', 90, 2.5],
      ['bone_golem', 150, 2.5],
      ['skeleton', 260, 6],
    ],
    swarmer: 'skeleton',
    flyer: 'wailing_ghost',
    elite: 'bone_golem',
    boss: 'lich_king',
    hazard: { kind: 'curse', interval: 9, damage: 5, color: '#a06bff', start: 40 },
    visuals: {
      sky: '#07060f', fog: '#0f0c1d', fogNear: 14, fogFar: 40,
      ground: ['#1f1d29', '#2a2838', '#13121b'], detail: 'stone', cracks: '#5dffb0',
      hemiSky: '#9a8cff', hemiGround: '#120f1a', hemi: 0.8, sun: '#b3a8ff', sunIntensity: 1.2,
      props: [{ kind: 'tomb', count: 36, solid: true }, { kind: 'pillar', count: 14, solid: true }, { kind: 'candle', count: 24, solid: false }],
      ambient: 'motes', accent: '#c4a8ff',
    },
    music: { root: 185, mode: [0, 1, 3, 5, 6, 8, 10], tempo: 88 },
  },
  {
    id: 'storm',
    name: 'Storm Peaks',
    subtitle: 'Above the clouds, the sky is angry',
    duration: 480,
    hpMult: 3.1,
    dmgMult: 1.8,
    rateMult: 1.22,
    xpMult: 1.2,
    roster: [
      ['harpy', 0, 8],
      ['storm_sprite', 40, 4],
      ['crag_hound', 80, 4],
      ['thunder_shaman', 120, 3],
      ['stone_giant', 170, 2.5],
    ],
    swarmer: 'harpy',
    flyer: 'harpy',
    elite: 'stone_giant',
    boss: 'thunder_titan',
    hazard: { kind: 'lightning', interval: 3.6, damage: 20, color: '#9fdcff', start: 30 },
    visuals: {
      sky: '#0c1119', fog: '#1a2230', fogNear: 16, fogFar: 46,
      ground: ['#3a3f47', '#4a5059', '#272b31'], detail: 'rock', cracks: '#6fc8ff',
      hemiSky: '#a9c8ff', hemiGround: '#2a2f38', hemi: 1.0, sun: '#d0e4ff', sunIntensity: 1.4,
      props: [{ kind: 'crag', count: 26, solid: true }, { kind: 'crystal', count: 20, solid: true }, { kind: 'rock', count: 16, solid: true }],
      ambient: 'rain', accent: '#7fd4ff',
    },
    music: { root: 233.08, mode: [0, 2, 4, 6, 7, 9, 11], tempo: 112 },
  },
  {
    id: 'void',
    name: 'Void Rift',
    subtitle: 'Reality ends here',
    duration: 480,
    hpMult: 3.3,
    dmgMult: 2.0,
    rateMult: 1.25,
    xpMult: 1.25,
    roster: [
      ['void_mite', 0, 10],
      ['void_splitter', 40, 4],
      ['rift_stalker', 80, 3],
      ['abyss_eye', 120, 3],
      ['void_behemoth', 170, 2],
    ],
    swarmer: 'void_mite',
    flyer: 'void_mite',
    elite: 'void_behemoth',
    boss: 'hollow_star',
    hazard: { kind: 'rift', interval: 14, damage: 14, color: '#ff3df2', start: 40 },
    visuals: {
      // An ordinary gravel plain under an overcast sky: the rifts and the purple horde are what's wrong with it.
      sky: '#aab2b8', fog: '#b4bac0', fogNear: 24, fogFar: 60,
      ground: ['#a2a29c', '#b2b2ab', '#92928c'], detail: 'sand', cracks: null,
      hemiSky: '#e4e8ec', hemiGround: '#6e6e68', hemi: 1.1, sun: '#f2f0ea', sunIntensity: 1.9,
      props: [{ kind: 'rock', count: 16, solid: true }, { kind: 'crag', count: 10, solid: true }, { kind: 'deadtree', count: 8, solid: true }],
      ambient: 'none', accent: '#ff7af2',
    },
    music: { root: 174.61, mode: [0, 1, 3, 4, 7, 8, 11], tempo: 100 },
  },
  {
    id: 'lut',
    name: 'Dasht-e Lut',
    subtitle: 'The hottest earth under the sun',
    duration: 480,
    hpMult: 3.6,
    dmgMult: 2.2,
    rateMult: 1.28,
    xpMult: 1.3,
    roster: [
      ['lut_scarab', 0, 10],
      ['lut_vulture', 35, 5],
      ['lut_scorpion', 75, 4],
      ['desert_ghoul', 115, 3],
      ['kalut_golem', 170, 2],
    ],
    swarmer: 'lut_scarab',
    flyer: 'lut_vulture',
    elite: 'kalut_golem',
    boss: 'azhdaha',
    hazard: { kind: 'sandstorm', interval: 22, damage: 6, color: '#ffd08a', start: 45 },
    visuals: {
      // Blinding daylight over pale sand: the horde is dark so it reads against the dunes.
      sky: '#e9c48a', fog: '#e0b47a', fogNear: 24, fogFar: 60,
      ground: ['#c79a5e', '#d9b277', '#a97c46'], detail: 'sand', cracks: null,
      hemiSky: '#fff0d4', hemiGround: '#8a5a2e', hemi: 1.6, sun: '#fff2d0', sunIntensity: 3.3,
      props: [{ kind: 'kalut', count: 20, solid: true }, { kind: 'dune_rock', count: 16, solid: true }, { kind: 'salt', count: 40, solid: false }],
      ambient: 'none', accent: '#ffb347',
    },
    music: { root: 196, mode: [0, 1, 4, 5, 7, 8, 10], tempo: 100 },
  },
  {
    id: 'mazandaran',
    name: 'Mazandaran Forest',
    subtitle: 'Where Rostam faced the divs',
    duration: 480,
    hpMult: 4.0,
    dmgMult: 2.4,
    rateMult: 1.3,
    xpMult: 1.35,
    roster: [
      ['div_spawn', 0, 10],
      ['forest_raven', 30, 5],
      ['div_shaman', 80, 3],
      ['div_witch', 110, 2.5],
      ['horned_div', 165, 2],
      ['div_spawn', 280, 6],
    ],
    swarmer: 'div_spawn',
    flyer: 'forest_raven',
    elite: 'horned_div',
    boss: 'white_div',
    hazard: { kind: 'rockfall', interval: 5, damage: 26, color: '#c9b79a', start: 40 },
    visuals: {
      sky: '#0d1a14', fog: '#14261d', fogNear: 14, fogFar: 40,
      ground: ['#1c2e1e', '#26402a', '#132015'], detail: 'grass', cracks: null,
      hemiSky: '#9ad0a8', hemiGround: '#10180f', hemi: 0.9, sun: '#cfe8d0', sunIntensity: 1.2,
      props: [{ kind: 'hornbeam', count: 38, solid: true }, { kind: 'boulder', count: 14, solid: true }, { kind: 'grass', count: 70, solid: false }],
      ambient: 'motes', accent: '#9dff8a',
    },
    music: { root: 185, mode: [0, 1, 3, 5, 7, 8, 10], tempo: 90 },
  },
  {
    id: 'damavand',
    name: 'Damavand',
    subtitle: 'The chained king stirs',
    duration: 480,
    hpMult: 4.4,
    dmgMult: 2.6,
    rateMult: 1.33,
    xpMult: 1.4,
    roster: [
      ['zahhak_serpent', 0, 10],
      ['ash_harpy', 30, 5],
      ['fire_div', 70, 4],
      ['serpent_charmer', 110, 2.5],
      ['magma_div', 160, 2],
    ],
    swarmer: 'zahhak_serpent',
    flyer: 'ash_harpy',
    elite: 'magma_div',
    boss: 'zahhak',
    hazard: { kind: 'eruption', interval: 4.5, damage: 30, color: '#ff4a1a', start: 35 },
    visuals: {
      // Snow on a live volcano: lava shows through the cracks.
      sky: '#101420', fog: '#1e2433', fogNear: 16, fogFar: 46,
      ground: ['#d6dde6', '#eef2f7', '#aeb8c4'], detail: 'snow', cracks: '#ff5a1f',
      hemiSky: '#e0ecff', hemiGround: '#5a4040', hemi: 1.0, sun: '#ffe0c8', sunIntensity: 1.6,
      props: [{ kind: 'chain_pillar', count: 14, solid: true }, { kind: 'crag', count: 18, solid: true }, { kind: 'pine', count: 16, solid: true }, { kind: 'ember', count: 30, solid: false }],
      ambient: 'embers', accent: '#ff7a2f',
    },
    music: { root: 220, mode: [0, 1, 4, 5, 7, 8, 11], tempo: 108 },
  },
];

export const spellById = (id: string): SpellDef => {
  const def = SPELLS.find((s) => s.id === id);
  if (!def) throw new Error(`Unknown spell ${id}`);
  return def;
};

export const passiveById = (id: string): PassiveDef => {
  const def = PASSIVES.find((p) => p.id === id);
  if (!def) throw new Error(`Unknown passive ${id}`);
  return def;
};

export const enemyById = (id: string): EnemyDef => {
  const def = ENEMIES[id];
  if (!def) throw new Error(`Unknown enemy ${id}`);
  return def;
};
